import { useMemo, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { ConfirmModal } from '@/components/ConfirmModal';

type DeleteModalProps = {
  closeModal?: () => void;
  setIsOpenDeleteModal: (open: boolean) => void;

  // These are passed as translation keys OR plain strings (t() will return the input if no key exists)
  title: string;
  heading: string;
  text: string;

  // Optional: show bulk selection count
  deleteUsers?: boolean;
  selectedUser?: Set<any> | any[] | null;

  // The only thing this component does: run this, then close
  onConfirm: () => Promise<any> | any;
};

const getSelectedCount = (selectedUser?: Set<any> | any[] | null) => {
  if (!selectedUser) return 0;
  if (selectedUser instanceof Set) return selectedUser.size || 0;
  if (Array.isArray(selectedUser)) return selectedUser.length || 0;
  return 0;
};

const DeleteModal = ({
  closeModal,
  setIsOpenDeleteModal,
  title,
  heading,
  text,
  deleteUsers,
  selectedUser,
  onConfirm,
}: DeleteModalProps) => {
  const { t } = useTranslation();

  const selectedCount = useMemo(
    () => getSelectedCount(selectedUser),
    [selectedUser]
  );

  const handleConfirm = async () => {
    try {
      await Promise.resolve(onConfirm?.());
    } finally {
      setIsOpenDeleteModal(false);
    }
  };
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        closeModal && closeModal();
      }
    };

    window.addEventListener('keydown', handleKeyDown);

    return () => {
      window.removeEventListener('keydown', handleKeyDown);
    };
  }, [closeModal]);

  return (
    <ConfirmModal
      open={true}
      onClose={closeModal || (() => setIsOpenDeleteModal(false))}
      onConfirm={handleConfirm}
      title={title}
      description="" // render body ourselves
      cancelLabel="button.cancel"
      confirmLabel="button.delete"
    >
      <div className="flex flex-col gap-5">
        <div>
          <h3 className="mb-3 text-lg">{t(heading)}</h3>
          <p className="text-base text-grey-06">{t(text)}</p>
        </div>

        {deleteUsers && selectedCount > 0 && (
          <div className="text-sm text-grey-06">
            {selectedCount} {t('pages.manage_users.selected')}
          </div>
        )}
      </div>
    </ConfirmModal>
  );
};

export default DeleteModal;
